// Phase B.3 — Milestone celebration card.
// Renders goal progress milestones (25/50/75/100%) with celebration
// state for newly reached thresholds. Renders inside chat bubbles.

import { Icon } from "../../../components/icons/Icon.tsx";

interface Milestone {
  readonly pct: number;
  readonly amount: number;
  readonly reached: boolean;
  readonly newly_reached: boolean;
}

interface MilestoneData {
  readonly goal_title?: string;
  readonly target_amount?: number;
  readonly current_amount?: number;
  readonly currency?: string;
  readonly progress_pct?: number;
  readonly milestones?: readonly Milestone[];
  readonly achieved?: boolean;
}

export function SikaMilestoneCard({ data }: { data: unknown }): JSX.Element {
  const d = (data ?? {}) as MilestoneData;
  const ccy = d.currency ?? "GH₵";
  const milestones = d.milestones ?? [];
  const progressPct = d.progress_pct ?? 0;
  const newlyReached = milestones.filter((m) => m.newly_reached);

  return (
    <div className="plan-card">
      <div className="plan-head">
        <div className="label">{d.achieved ? "Goal Achieved" : "Milestone Update"}</div>
        <div className="ttl">{d.goal_title ?? "Goal Progress"}</div>
        <div className="meta">
          {d.current_amount != null && d.target_amount != null
            ? `${ccy}${Number(d.current_amount).toLocaleString()} of ${ccy}${Number(d.target_amount).toLocaleString()} — ${String(progressPct)}%`
            : `${String(progressPct)}% progress`}
        </div>
      </div>

      <div className="plan-section">
        <h4>Milestones</h4>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {milestones.map((m) => (
            <div
              key={m.pct}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "8px 12px",
                borderRadius: 8,
                background: m.newly_reached
                  ? "var(--pos-soft)"
                  : m.reached
                    ? "var(--bg-elev-2)"
                    : "transparent",
                border: `1px solid ${m.reached ? "var(--border-faint)" : "var(--border-faint)"}`,
              }}
            >
              <Icon name={m.reached ? "check" : "close"} size={14} />
              <span
                style={{
                  fontSize: 13,
                  fontWeight: m.newly_reached ? 600 : 400,
                  color: m.reached ? "var(--text)" : "var(--text-tertiary)",
                }}
              >
                {m.pct}% — {ccy}{Number(m.amount).toLocaleString()}
              </span>
              {m.newly_reached && (
                <span
                  style={{
                    marginLeft: "auto",
                    fontSize: 10,
                    fontWeight: 600,
                    padding: "2px 7px",
                    borderRadius: 999,
                    background: "var(--pos-soft)",
                    color: "var(--pos)",
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                  }}
                >
                  Reached
                </span>
              )}
            </div>
          ))}
        </div>
      </div>

      {newlyReached.length > 0 && (
        <div className="plan-section">
          <div
            style={{
              padding: "10px 14px",
              background: "var(--pos-soft)",
              borderRadius: 9,
              fontSize: 13,
              lineHeight: 1.5,
              color: "var(--text)",
            }}
          >
            {d.achieved
              ? "You hit your target. Well done."
              : `${String(newlyReached.length)} new milestone${newlyReached.length > 1 ? "s" : ""} reached.`}
          </div>
        </div>
      )}

      <div className="plan-disclosure">
        Progress based on linked chain value. Actual outcomes depend on chain performance.
      </div>
    </div>
  );
}
